'use client'

import styled from '@emotion/styled'
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin'
import DownloadIcon from '@mui/icons-material/Download'
import MessageIcon from '@mui/icons-material/Message'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import Box from '@mui/material/Box'
import MuiButton from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'
import Image from 'next/image'

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #0a1929 0%, #0f2942 50%, #0a1929 100%);
  overflow: hidden;
  padding-top: var(--header-height);
`

const Badge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(39, 177, 198, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(39, 177, 198, 0.2);
  border-radius: 9999px;
  padding: 6px 16px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #27b1c6;
`

const StatValue = styled(Typography)`
  font-size: clamp(1.5rem, 2vw, 1.875rem);
  font-weight: 700;
  color: #27b1c6;
`

const StatLabel = styled(Typography)`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`

const FloatingIcon = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $bg: string; $border: string }>`
  position: absolute;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  backdrop-filter: blur(8px);
  border: 1px solid ${({ $border }) => $border};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 30;
`

const PhoneFrame = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(39, 177, 198, 0.2);

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`

export default function HeroSection() {
  return (
    <Section>
      {/* Background blur orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: 80,
          right: 80,
          width: 384,
          height: 384,
          background: 'rgba(39, 177, 198, 0.2)',
          borderRadius: '50%',
          filter: 'blur(96px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 80,
          left: 80,
          width: 384,
          height: 384,
          background: 'rgba(39, 177, 198, 0.1)',
          borderRadius: '50%',
          filter: 'blur(96px)',
        }}
      />

      <Container
        maxWidth='lg'
        sx={{ position: 'relative', zIndex: 1, py: { xs: 8, lg: 10 } }}
      >
        <Grid container spacing={{ xs: 4, lg: 8 }} alignItems='center'>
          {/* Left: Text */}
          <Grid size={{ xs: 12, lg: 7 }} sx={{ order: { xs: 2, lg: 1 } }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge sx={{ mb: 3 }}>
                <SmartphoneIcon sx={{ fontSize: 16 }} />
                <span>Download Our Mobile App</span>
              </Badge>

              <Typography
                variant='h1'
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                  lineHeight: 1.15,
                  color: 'white',
                  mb: 3,
                }}
              >
                Neural Network{' '}
                <Box
                  component='span'
                  sx={{
                    background: 'linear-gradient(90deg, #14b8a6, #06b6d4)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Algorithmic Trading
                </Box>{' '}
                System
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1rem', lg: '1.25rem' },
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.7,
                  mb: 4,
                }}
              >
                Advanced AI-powered trading platform utilizing multi-timeframe
                analysis and high-frequency execution in cryptocurrency markets.
                Proven performance with 3% max. drawdown.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  mb: 5,
                }}
              >
                <MuiButton
                  component={Link}
                  href='/get-app'
                  variant='contained'
                  size='large'
                  startIcon={<DownloadIcon />}
                  sx={{
                    backgroundColor: '#27b1c6',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#1e8899', color: 'white' },
                    color: 'white',
                  }}
                >
                  Download App
                </MuiButton>
                <MuiButton
                  component={Link}
                  href='/consultation'
                  variant='outlined'
                  size='large'
                  startIcon={<MessageIcon />}
                  sx={{
                    borderColor: 'rgba(39,177,198,0.3)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    textTransform: 'none',
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                      backgroundColor: 'rgba(39,177,198,0.1)',
                      borderColor: 'rgba(39,177,198,0.5)',
                    },
                  }}
                >
                  Institutional Inquiry
                </MuiButton>
              </Box>

              <Grid
                container
                spacing={3}
                sx={{ pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}
              >
                <Grid size={4}>
                  <StatValue>3%</StatValue>
                  <StatLabel>Max. Drawdown</StatLabel>
                </Grid>
                <Grid size={4}>
                  <StatValue>&lt;50ms</StatValue>
                  <StatLabel>Prediction Latency</StatLabel>
                </Grid>
                <Grid size={4}>
                  <StatValue>20.000</StatValue>
                  <StatLabel>Trades Per Day</StatLabel>
                </Grid>
              </Grid>
            </motion.div>
          </Grid>

          {/* Right: Phone mockups + floating crypto icons */}
          <Grid
            size={{ xs: 12, lg: 5 }}
            sx={{
              order: { xs: 1, lg: 2 },
              position: 'relative',
              minHeight: { xs: 420, sm: 480, lg: 580 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: { xs: 14, lg: 0 },
            }}
          >
            {/* Floating Bitcoin - top left */}
            <FloatingIcon
              $bg='linear-gradient(135deg, rgba(249,115,22,0.2), rgba(249,115,22,0.1))'
              $border='rgba(249,115,22,0.3)'
              style={{ top: '-2%', left: '5%', width: 64, height: 64 }}
              animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <CurrencyBitcoinIcon sx={{ fontSize: 32, color: '#f97316' }} />
            </FloatingIcon>

            {/* Floating Ethereum - top right */}
            <FloatingIcon
              $bg='linear-gradient(135deg, rgba(59,130,246,0.2), rgba(59,130,246,0.1))'
              $border='rgba(59,130,246,0.3)'
              style={{ top: '3%', right: '-2%', width: 56, height: 56 }}
              animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 28, color: '#3b82f6' }}>
                Ξ
              </Typography>
            </FloatingIcon>

            {/* Floating Tether - bottom left */}
            <FloatingIcon
              $bg='linear-gradient(135deg, rgba(6,182,212,0.2), rgba(6,182,212,0.1))'
              $border='rgba(6,182,212,0.3)'
              style={{ bottom: '8%', left: '2%', width: 48, height: 48 }}
              animate={{ y: [0, -15, 0], rotate: [0, 15, 0] }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 24, color: '#06b6d4' }}>
                ₮
              </Typography>
            </FloatingIcon>

            {/* Floating Solana - bottom right */}
            <FloatingIcon
              $bg='linear-gradient(135deg, rgba(156,163,175,0.2), rgba(156,163,175,0.1))'
              $border='rgba(156,163,175,0.3)'
              style={{ bottom: '-2%', right: '0%', width: 56, height: 56 }}
              animate={{ y: [0, 15, 0], rotate: [0, -12, 0] }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.5,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 28, color: '#9ca3af' }}>
                ◎
              </Typography>
            </FloatingIcon>

            {/* Back phone - Transparency */}
            <PhoneFrame
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 0.85, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              style={{
                position: 'absolute',
                left: '10%',
                top: '2%',
                width: '52%',
                maxWidth: 280,
                transform: 'rotate(-12deg)',
                zIndex: 10,
              }}
            >
              <Image
                src='/assets/hero/app-transparency.jpg'
                alt='Performance Transparency'
                width={280}
                height={560}
                style={{ width: '100%', height: 'auto', position: 'relative' }}
              />
            </PhoneFrame>

            {/* Front phone - Portfolio */}
            <PhoneFrame
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              style={{
                position: 'absolute',
                left: '38%',
                top: '5%',
                width: '56%',
                maxWidth: 310,
                zIndex: 20,
              }}
            >
              <Image
                src='/assets/hero/app-portfolio.jpg'
                alt='Polar Tensor Mobile App'
                width={310}
                height={620}
                style={{ width: '100%', height: 'auto', position: 'relative' }}
              />
            </PhoneFrame>
          </Grid>
        </Grid>
      </Container>
    </Section>
  )
}
